/**
 * AnomaliesPage — supervisor review surface for anomaly_alerts
 * (PROJECT_STATE task #68).
 *
 * Lists open alerts first (sorted by severity + recency), then a
 * collapsed section of recently reviewed (last 7 days). Each open
 * alert exposes Confirm + Dismiss with an optional reviewer notes
 * field — both actions are explicit "reviewer must confirm" surfaces
 * per the Director's task spec.
 *
 * RLS does the visibility scoping (super_admin all; supervisor +
 * contractor_admin own org). The page itself is gated by RequireRole
 * in the router config.
 */

import { useState, useMemo, type ReactNode } from 'react'
import { useTranslation }                     from 'react-i18next'
import {
  useAnomalyAlerts,
  SEVERITY_RANK,
  type AnomalyAlertRow,
  type AnomalyCategory,
  type AnomalySeverity,
} from '@/hooks/useAnomalyAlerts'
import { useReviewAnomalyAlert } from '@/hooks/useReviewAnomalyAlert'
import { AppError }              from '@/lib/errors/AppError'

const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000

export function AnomaliesPage(): ReactNode {
  const { t } = useTranslation()
  const { data, isLoading, error, dataUpdatedAt } = useAnomalyAlerts()

  // The 7-day window is measured from when this data was fetched, not from
  // render time. `Date.now()` during render is impure — it makes the memo
  // return a different partition for identical inputs. `dataUpdatedAt` is a
  // stable number from the query cache that advances on every refetch, so
  // the window still moves, it just moves with the data instead of with the
  // render. Before any successful fetch it is 0, and `data` is undefined, so
  // the loop below has nothing to partition.
  const { open, recentlyReviewed } = useMemo(() => {
    const list = data ?? []
    const cutoff = dataUpdatedAt - SEVEN_DAYS_MS
    const open: AnomalyAlertRow[] = []
    const reviewed: AnomalyAlertRow[] = []
    for (const row of list) {
      if (row.status === 'open') open.push(row)
      else if (row.reviewed_at && new Date(row.reviewed_at).getTime() > cutoff) reviewed.push(row)
    }
    open.sort((a, b) => {
      const sev = SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity]
      if (sev !== 0) return sev
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
    reviewed.sort((a, b) =>
      new Date(b.reviewed_at ?? 0).getTime() - new Date(a.reviewed_at ?? 0).getTime(),
    )
    return { open, recentlyReviewed: reviewed }
  }, [data, dataUpdatedAt])

  return (
    <section style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <header>
        <h1 style={{ margin: 0, fontSize: '1.4rem', fontFamily: 'var(--brand-font-family-display)' }}>
          {t('anomalies.title', 'Anomaly alerts')}
        </h1>
        <p style={{ margin: '0.25rem 0 0', color: 'var(--brand-neutral-500)' }}>
          {t('anomalies.subtitle', 'Cross-cycle scan results — confirm or dismiss to clear.')}
        </p>
      </header>

      {isLoading && <Hint>{t('anomalies.loading', 'Loading alerts…')}</Hint>}
      {error    && (
        <ErrorPanel
          message={error instanceof Error ? error.message : t('anomalies.failedToLoad', 'Failed to load anomaly alerts.')}
        />
      )}

      {!isLoading && !error && open.length === 0 && (
        <Hint>{t('anomalies.empty', 'No open anomaly alerts. Nice work.')}</Hint>
      )}

      {!isLoading && !error && open.length > 0 && (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
          {open.map((row) => <AlertRow key={row.id} row={row} />)}
        </ul>
      )}

      {!isLoading && !error && recentlyReviewed.length > 0 && (
        <details style={{ marginTop: '0.5rem' }}>
          <summary style={{ cursor: 'pointer', color: 'var(--brand-neutral-500)' }}>
            {t('anomalies.recentReviewed', 'Recently reviewed ({{count}})', { count: recentlyReviewed.length })}
          </summary>
          <ul style={{ margin: '0.6rem 0 0', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
            {recentlyReviewed.map((row) => <ReviewedRow key={row.id} row={row} />)}
          </ul>
        </details>
      )}
    </section>
  )
}

// ─────────────────────────────────────────────────────────────────────

function AlertRow({ row }: { row: AnomalyAlertRow }): ReactNode {
  const { t }    = useTranslation()
  const review   = useReviewAnomalyAlert()
  const [notes,       setNotes]       = useState('')
  const [showNotes,   setShowNotes]   = useState(false)
  const [actionError, setActionError] = useState<AppError | null>(null)

  const onConfirm = async (): Promise<void> => {
    setActionError(null)
    try {
      await review.mutateAsync({ alert: row, mode: 'confirm', reviewerNotes: notes })
      setNotes(''); setShowNotes(false)
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }
  const onDismiss = async (): Promise<void> => {
    setActionError(null)
    try {
      await review.mutateAsync({ alert: row, mode: 'dismiss', reviewerNotes: notes })
      setNotes(''); setShowNotes(false)
    } catch (e) {
      setActionError(e instanceof AppError ? e : null)
    }
  }

  return (
    <li
      data-testid="anomaly-alert"
      style={{
        background:   'var(--brand-white)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.75rem 1rem',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.5rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
        <CategoryBadge category={row.category} />
        <SeverityBadge severity={row.severity} />
        <span style={{ color: 'var(--brand-neutral-500)', fontSize: '0.8rem' }}>
          {t('anomalies.detected', 'detected {{when}}', { when: new Date(row.created_at).toLocaleString() })}
        </span>
      </div>

      {row.ai_summary && (
        <p style={{ margin: 0, fontSize: '0.95rem' }}>{row.ai_summary}</p>
      )}
      {!row.ai_summary && (
        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--brand-neutral-500)', fontStyle: 'italic' }}>
          {t('anomalies.aiUnavailable', 'AI summary unavailable — review the metrics below.')}
        </p>
      )}

      <MetricsBlock metrics={row.metrics_json} />

      {actionError && <ErrorPanel message={actionError.userMessage} />}

      {showNotes && (
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          disabled={review.isPending}
          placeholder={t('anomalies.notesPlaceholder', 'Optional reviewer notes') ?? ''}
          style={{
            padding:      '0.5rem',
            border:       '1px solid var(--brand-neutral-300)',
            borderRadius: 'var(--brand-radius-sm)',
            fontFamily:   'inherit',
            fontSize:     '0.9rem',
            resize:       'vertical',
          }}
        />
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <Button kind="primary" disabled={review.isPending} onClick={onConfirm}>
          {review.isPending ? t('anomalies.working', 'Working…') : t('anomalies.confirm', 'Confirm')}
        </Button>
        <Button kind="secondary" disabled={review.isPending} onClick={onDismiss}>
          {t('anomalies.dismiss', 'Dismiss')}
        </Button>
        <Button kind="secondary" disabled={review.isPending} onClick={() => setShowNotes((v) => !v)}>
          {showNotes
            ? t('anomalies.hideNotes', 'Hide notes')
            : t('anomalies.addNotes', 'Add notes')}
        </Button>
      </div>
    </li>
  )
}

function ReviewedRow({ row }: { row: AnomalyAlertRow }): ReactNode {
  return (
    <li
      data-testid="anomaly-alert-reviewed"
      data-status={row.status}
      style={{
        background:   'var(--brand-neutral-100)',
        border:       '1px solid var(--brand-neutral-300)',
        borderRadius: 'var(--brand-radius-sm)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
        display:      'flex',
        alignItems:   'center',
        gap:          '0.5rem',
        flexWrap:     'wrap',
        color:        'var(--brand-neutral-700)',
      }}
    >
      <CategoryBadge category={row.category} />
      <SeverityBadge severity={row.severity} />
      <span>{row.status === 'confirmed' ? '✓ confirmed' : '✗ dismissed'}</span>
      <span style={{ color: 'var(--brand-neutral-500)' }}>
        · {row.reviewed_at ? new Date(row.reviewed_at).toLocaleString() : '—'}
      </span>
      {row.reviewer_notes && <span style={{ marginLeft: 'auto', fontStyle: 'italic' }}>"{row.reviewer_notes}"</span>}
    </li>
  )
}

function MetricsBlock({ metrics }: { metrics: Record<string, unknown> }): ReactNode {
  const entries = Object.entries(metrics).filter(([, v]) => v !== null && v !== undefined)
  if (entries.length === 0) return null
  return (
    <ul
      data-testid="anomaly-metrics"
      style={{
        margin:       0,
        padding:      '0.4rem 0.5rem',
        background:   'var(--brand-neutral-100)',
        borderRadius: 'var(--brand-radius-sm)',
        listStyle:    'none',
        fontSize:     '0.8rem',
        color:        'var(--brand-neutral-700)',
        display:      'flex',
        flexDirection:'column',
        gap:          '0.15rem',
      }}
    >
      {entries.map(([k, v]) => (
        <li key={k}>
          <strong>{k.replace(/_/g, ' ')}:</strong>{' '}
          {Array.isArray(v) ? v.map(String).join(', ') : String(v)}
        </li>
      ))}
    </ul>
  )
}

function CategoryBadge({ category }: { category: AnomalyCategory }): ReactNode {
  const label: Record<AnomalyCategory, string> = {
    repeat_redo:                'Repeat re-do',
    off_hours_approval:         'Off-hours approval',
    transition_failure_cluster: 'Failure cluster',
  }
  return (
    <span
      data-category={category}
      style={{
        fontSize:     '0.7rem',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   'var(--brand-primary-subtle)',
        color:        'var(--brand-primary)',
        fontWeight:   600,
      }}
    >
      {label[category]}
    </span>
  )
}

function SeverityBadge({ severity }: { severity: AnomalySeverity }): ReactNode {
  const palette: Record<AnomalySeverity, { bg: string; fg: string }> = {
    high:   { bg: 'var(--brand-danger-subtle)',  fg: 'var(--brand-danger)'  },
    medium: { bg: 'var(--brand-primary-subtle)', fg: 'var(--brand-primary)' },
    low:    { bg: 'var(--brand-neutral-100)',    fg: 'var(--brand-neutral-700)' },
  }
  const c = palette[severity]
  return (
    <span
      data-severity={severity}
      style={{
        fontSize:     '0.7rem',
        padding:      '0.1rem 0.45rem',
        borderRadius: 'var(--brand-radius-full)',
        background:   c.bg,
        color:        c.fg,
        fontWeight:   600,
        textTransform:'capitalize',
      }}
    >
      {severity}
    </span>
  )
}

interface ButtonProps {
  kind:     'primary' | 'secondary'
  disabled: boolean
  onClick:  () => void | Promise<void>
  children: ReactNode
}

function Button({ kind, disabled, onClick, children }: ButtonProps): ReactNode {
  const palette: Record<ButtonProps['kind'], { bg: string; fg: string; border: string }> = {
    primary:   { bg: 'var(--brand-primary)',     fg: 'var(--brand-white)',       border: 'var(--brand-primary)'    },
    secondary: { bg: 'var(--brand-neutral-100)', fg: 'var(--brand-neutral-900)', border: 'var(--brand-neutral-300)' },
  }
  const c = palette[kind]
  return (
    <button
      type="button"
      onClick={() => { void onClick() }}
      disabled={disabled}
      style={{
        background:   c.bg,
        color:        c.fg,
        border:       `1px solid ${c.border}`,
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.45rem 0.8rem',
        fontWeight:   600,
        fontSize:     '0.85rem',
        cursor:       disabled ? 'not-allowed' : 'pointer',
        opacity:      disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

function Hint({ children }: { children: ReactNode }): ReactNode {
  return <p style={{ color: 'var(--brand-neutral-500)' }}>{children}</p>
}

function ErrorPanel({ message }: { message: string }): ReactNode {
  return (
    <div
      role="alert"
      style={{
        background:   'var(--brand-danger-subtle)',
        color:        'var(--brand-danger)',
        border:       '1px solid var(--brand-danger)',
        borderRadius: 'var(--brand-radius-md)',
        padding:      '0.5rem 0.75rem',
        fontSize:     '0.85rem',
      }}
    >
      {message}
    </div>
  )
}

